// GanttConnections.jsx
import React, { useMemo } from 'react';

const PALETA_KOLOROW = [
  '#3f51b5', '#f4a261', '#e63946', '#2a9d8f', '#9c27b0',
  '#e91e63', '#00bcd4', '#ff9800', '#8bc34a', '#795548',
];

const KOLOR_DOMYSLNY = '#888888';

const czyMajaWspolnyArkusz = (arkuszeA, arkuszeB) => {
  if (!arkuszeA || !arkuszeB) return true;

  const listA = Array.isArray(arkuszeA)
    ? arkuszeA.map(String)
    : String(arkuszeA).split(',').map(s => s.trim()).filter(Boolean);

  const listB = Array.isArray(arkuszeB)
    ? arkuszeB.map(String)
    : String(arkuszeB).split(',').map(s => s.trim()).filter(Boolean);

  if (listA.length === 0 || listB.length === 0) return true;

  return listA.some(arkusz => listB.includes(arkusz));
};

const GanttConnections = ({
  groupedStages = {},
  processorIds = [],
  rowYPositions = {},
  minDate,
  maxDate,
  totalChartWidth,
  selectedZamowienieId,
  showAllConections,
  showElementConections,
  calculateBarProperties
}) => {
  const connectionLines = useMemo(() => {
    // Renderujemy linie WYŁĄCZNIE gdy włączone są połączenia elementów
    if (!showElementConections) {
      return [];
    }

    if (!minDate || !maxDate || !totalChartWidth || typeof calculateBarProperties !== 'function') {
      return [];
    }

    const stagesByGroupAndIndex = {};
    const elementColorMap = {};

    const selectedZamowienieIdStr = selectedZamowienieId !== undefined && selectedZamowienieId !== null 
      ? String(selectedZamowienieId) 
      : null;

    let processedStagesCount = 0;

    processorIds.forEach((processorId) => {
      const rowStages = groupedStages[processorId] || [];
      const defaultRowY = rowYPositions[processorId] ?? 0;

      rowStages.forEach((stage) => {
        if (!stage || stage.zamowienie_id === undefined || stage.zamowienie_id === null) return;

        const zamowienieIdStr = String(stage.zamowienie_id);
        const y = stage._centerY ?? defaultRowY;

        // Wyświetlamy linie tylko dla zaznaczonego zamówienia i etapów posiadających element_id
        if (zamowienieIdStr !== selectedZamowienieIdStr) return;
        if (stage.element_id === undefined || stage.element_id === null) return;

        processedStagesCount++;

        const elementIdStr = String(stage.element_id);

        if (!(elementIdStr in elementColorMap)) {
          const currentIndex = Object.keys(elementColorMap).length;
          elementColorMap[elementIdStr] = currentIndex < PALETA_KOLOROW.length
            ? PALETA_KOLOROW[currentIndex]
            : KOLOR_DOMYSLNY;
        }

        const groupKey = `elem_${zamowienieIdStr}_${elementIdStr}`;

        const indeks = Number(stage.indeks_procesu ?? stage.indeks ?? stage.krok ?? 0);

        const barProps = calculateBarProperties(stage.start, stage.end);
        const leftPx = Number(barProps?.leftPx);
        const widthPx = Number(barProps?.widthPx);

        if (Number.isNaN(leftPx) || Number.isNaN(widthPx)) return;

        if (!stagesByGroupAndIndex[groupKey]) {
          stagesByGroupAndIndex[groupKey] = {};
        }

        if (!stagesByGroupAndIndex[groupKey][indeks]) {
          stagesByGroupAndIndex[groupKey][indeks] = [];
        }

        stagesByGroupAndIndex[groupKey][indeks].push({
          stageId: String(stage.global_id || stage.id),
          zamowienieId: zamowienieIdStr,
          elementId: elementIdStr,
          arkuszeId: stage.arkusze_id || stage.arkusz_id || stage.arkusze,
          indeksProcesu: indeks,
          outPoint: { x: leftPx + widthPx, y },
          inPoint: { x: leftPx, y }
        });
      });
    });

    if (processedStagesCount === 0) return [];

    const lines = [];

    Object.keys(stagesByGroupAndIndex).forEach((groupKey) => {
      const indexesMap = stagesByGroupAndIndex[groupKey];
      
      const sortedIndexes = Object.keys(indexesMap)
        .map(Number)
        .sort((a, b) => a - b);

      for (let i = 0; i < sortedIndexes.length - 1; i++) {
        const currentIndeks = sortedIndexes[i];
        const nextIndeks = sortedIndexes[i + 1];

        const currentStages = indexesMap[currentIndeks];
        const nextStages = indexesMap[nextIndeks];

        currentStages.forEach((fromStage) => {
          nextStages.forEach((toStage) => {
            const wspolneArkusze = czyMajaWspolnyArkusz(fromStage.arkuszeId, toStage.arkuszeId);

            if (!wspolneArkusze) return;

            const strokeColor = elementColorMap[fromStage.elementId] || KOLOR_DOMYSLNY;

            lines.push({
              id: `${groupKey}_${fromStage.stageId}_to_${toStage.stageId}`,
              zamowienieId: fromStage.zamowienieId,
              elementId: fromStage.elementId,
              from: fromStage.outPoint,
              to: toStage.inPoint,
              strokeColor
            });
          });
        });
      }
    });

    return lines;
  }, [
    groupedStages,
    processorIds,
    rowYPositions,
    minDate,
    maxDate,
    totalChartWidth,
    selectedZamowienieId,
    showElementConections,
    calculateBarProperties
  ]);

  if (connectionLines.length === 0) return null;

  const uniqueElements = Array.from(
    new Map(
      connectionLines
        .filter(line => line.elementId !== null && line.elementId !== undefined)
        .map(line => [line.elementId, line.strokeColor])
    ).entries()
  );

  return (
    <svg
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        zIndex: 9
      }}
    >
      <defs>
        {uniqueElements.map(([elementId, color]) => (
          <marker
            key={`marker-${elementId}`}
            id={`arrowhead-elem-${elementId}`}
            markerWidth="6"
            markerHeight="6"
            refX="5"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 6 3, 0 6" fill={color} />
          </marker>
        ))}
      </defs>

      {connectionLines.map((line) => {
        const { from, to, id, strokeColor, elementId } = line;
        const deltaX = Math.max(20, Math.abs(to.x - from.x) / 2);
        const pathData = `M ${from.x} ${from.y} C ${from.x + deltaX} ${from.y}, ${to.x - deltaX} ${to.y}, ${to.x} ${to.y}`;
        const markerId = `url(#arrowhead-elem-${elementId})`;

        return (
          <g key={`connection-${id}`}>
            <path
              d={pathData}
              fill="none"
              stroke={strokeColor}
              strokeWidth="1"
              markerEnd={markerId}
            />
            <circle cx={from.x} cy={from.y} r="3.5" fill={strokeColor} />
          </g>
        );
      })}
    </svg>
  );
};

export default React.memo(GanttConnections);